import { useEffect, useState } from 'react';
import { db, type VideoGeneration } from '../db';
import { apiClient } from '../api';
import { Play, Clock, AlertCircle, Loader2, Grid, Layers, Download } from 'lucide-react';
import GenerationAlert from '../components/GenerationAlert';
import { useGenerationAlert } from '../hooks/useGenerationAlert';

const HistoryPage = () => {
    const [videos, setVideos] = useState<VideoGeneration[]>([]);
    const [filter, setFilter] = useState<'all' | 'single' | 'batch'>('all');
    const [loading, setLoading] = useState(true);
    const { alertState, showErrorAlert, hideAlert } = useGenerationAlert();

    const fetchVideos = async () => {
        const allVideos = await db.getAllGenerations();
        // Sort by date desc
        allVideos.sort((a, b) => b.createdAt - a.createdAt);
        setVideos(allVideos);
        setLoading(false);
    };

    // Poll for status updates
    useEffect(() => {
        fetchVideos();

        const pollInterval = setInterval(async () => {
            try {
                const data = await apiClient.getStatus();

                // Update local DB with new statuses
                const allVideos = await db.getAllGenerations();
                let hasUpdates = false;

                for (const remoteVideo of data.videos) {
                    const localVideo = allVideos.find((v: VideoGeneration) => v.id === remoteVideo.id);
                    if (localVideo && (localVideo.status !== remoteVideo.status || !localVideo.videoUrl)) {
                        await db.updateStatus(
                            remoteVideo.id,
                            remoteVideo.status,
                            remoteVideo.videoUrl,
                            remoteVideo.thumbnailUrl
                        );
                        hasUpdates = true;
                    }
                }

                if (hasUpdates) {
                    fetchVideos();
                }
            } catch (err) {
                console.error('Polling error', err);
                // Handle authorization errors gracefully
                if (err instanceof Error && err.message.includes('Authorization')) {
                    showErrorAlert('Authorization required. Please configure your authorization key in settings.');
                }
            }
        }, 5000);

        return () => clearInterval(pollInterval);
    }, []);

    const filteredVideos = videos.filter(v => {
        if (filter === 'all') return true;
        return v.mode === filter;
    });

    // Group batch videos by batchName
    const groupedBatchVideos = () => {
        const groups: Record<string, VideoGeneration[]> = {};
        filteredVideos.forEach(video => {
            if (video.mode === 'batch') {
                const batchName = video.batchName || 'Unnamed Batch';
                if (!groups[batchName]) {
                    groups[batchName] = [];
                }
                groups[batchName].push(video);
            }
        });
        return groups;
    };

    // Download single video
    const downloadVideo = async (video: VideoGeneration) => {
        if (!video.videoUrl) return;

        try {
            const response = await fetch(video.videoUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            // Create filename from prompt (first 50 chars) + timestamp
            const filename = `${video.prompt.slice(0, 50).replace(/[^a-z0-9]/gi, '_')}_${video.id}.mp4`;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Download failed', err);
            alert('Failed to download video');
        }
    };

    // Download all videos in a batch
    const downloadAllVideos = async (videos: VideoGeneration[], batchName: string) => {
        const completedVideos = videos.filter(v => v.status === 'completed' && v.videoUrl);

        if (completedVideos.length === 0) {
            alert('No completed videos to download');
            return;
        }

        for (let i = 0; i < completedVideos.length; i++) {
            const video = completedVideos[i];
            try {
                const response = await fetch(video.videoUrl!);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                // Create filename with batch name and index
                const filename = `${batchName.replace(/[^a-z0-9]/gi, '_')}_${i + 1}_${video.id}.mp4`;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);

                // Small delay between downloads
                if (i < completedVideos.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            } catch (err) {
                console.error(`Failed to download video ${i + 1}`, err);
            }
        }
    };

    const renderVideoCard = (video: VideoGeneration) => (
        <div key={video.id} className="video-card">
            <div className={`video-preview ${video.aspectRatio}`}>
                {video.status === 'completed' && video.videoUrl ? (
                    <>
                        <video controls preload="metadata" poster={video.thumbnailUrl}>
                            <source src={video.videoUrl} type="video/mp4" />
                        </video>
                        <button
                            onClick={() => downloadVideo(video)}
                            className="btn-icon"
                            style={{
                                position: 'absolute',
                                top: '0.5rem',
                                right: '0.5rem',
                                background: 'rgba(0, 0, 0, 0.7)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                zIndex: 10
                            }}
                            title="Download video"
                        >
                            <Download size={18} />
                        </button>
                    </>
                ) : (
                    <div className="status-overlay">
                        {video.status === 'queued' && <Clock className="status-icon queued" />}
                        {video.status === 'processing' && <Loader2 className="status-icon processing animate-spin" />}
                        {video.status === 'failed' && <AlertCircle className="status-icon failed" />}
                        <span className={`status-text ${video.status}`}>{video.status}</span>
                    </div>
                )}
                <div className="badge-container">
                    <span className="mode-badge">{video.mode}</span>
                </div>
            </div>
            <div className="video-details">
                <p className="video-prompt" title={video.prompt}>{video.prompt}</p>
                <div className="video-meta">
                    <span>{new Date(video.createdAt).toLocaleTimeString()}</span>
                    <span>{video.aspectRatio}</span>
                </div>
            </div>
        </div>
    );

    return (
        <div className="page-container">
            <header className="page-header">
                <div className="header-content">
                    <div>
                        <h2>History</h2>
                        <p>View and manage your generated videos.</p>
                    </div>
                </div>
            </header>

            <div className="tabs" style={{ marginBottom: '2rem' }}>
                <button
                    className={`tab ${filter === 'all' ? 'active' : ''}`}
                    onClick={() => setFilter('all')}
                >
                    <Grid size={18} /> All Videos
                </button>
                <button
                    className={`tab ${filter === 'single' ? 'active' : ''}`}
                    onClick={() => setFilter('single')}
                >
                    <Play size={18} /> Single
                </button>
                <button
                    className={`tab ${filter === 'batch' ? 'active' : ''}`}
                    onClick={() => setFilter('batch')}
                >
                    <Layers size={18} /> Batch
                </button>
            </div>

            {loading ? (
                <div className="loading-state">Loading history...</div>
            ) : (
                <>
                    {filter === 'batch' ? (
                        // Batch view with grouping
                        <div>
                            {Object.entries(groupedBatchVideos()).map(([batchName, batchVideos]) => (
                                <div key={batchName} style={{ marginBottom: '3rem' }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: '1rem'
                                    }}>
                                        <h3 style={{
                                            fontSize: '1.25rem',
                                            fontWeight: '600',
                                            color: '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            margin: 0
                                        }}>
                                            <Layers size={20} />
                                            {batchName}
                                            <span style={{
                                                fontSize: '0.875rem',
                                                fontWeight: '400',
                                                color: '#888',
                                                marginLeft: '0.5rem'
                                            }}>
                                                ({batchVideos.length} {batchVideos.length === 1 ? 'video' : 'videos'})
                                            </span>
                                        </h3>
                                        <button
                                            onClick={() => downloadAllVideos(batchVideos, batchName)}
                                            className="btn-secondary"
                                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                        >
                                            <Download size={18} />
                                            Download All
                                        </button>
                                    </div>
                                    <div className="video-grid">
                                        {batchVideos.map(renderVideoCard)}
                                    </div>
                                </div>
                            ))}
                            {Object.keys(groupedBatchVideos()).length === 0 && (
                                <div className="empty-state">
                                    No batch videos found.
                                </div>
                            )}
                        </div>
                    ) : (
                        // All and Single view - regular grid
                        <div className="video-grid">
                            {filteredVideos.map(renderVideoCard)}

                            {filteredVideos.length === 0 && (
                                <div className="empty-state">
                                    No videos found.
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            <GenerationAlert
                isVisible={alertState.isVisible}
                type={alertState.type}
                title={alertState.title}
                message={alertState.message}
                onClose={hideAlert}
            />
        </div>
    );
};

export default HistoryPage;
